import { AxiosProgressEvent } from "axios";
import { fetchTrainingStatus } from "../../store/slice/ProjectTrainSlice";

// Define the type for the API response
export interface ProjectTrain {
  id: string;
  name: string;
  trainingId?: string;
  imageCount?: number;
  labelCount?: number;
  modelSize?: number;
  status?: string;
}

export interface ProjectTrainDetail {
  id: string;
  modelUrl: string;
  name: string;
  description: string;
  epochs: number;
  status: string;
  advanceSettingJson: string;
  advanceSetting: {
    modelTypeId: string;
    modelQuality: string;
    augmentations: {
      id: string;
      horizontalFlip: boolean;
      verticalFlip: boolean;
      randomRotate: boolean;
      randomCrop: boolean;
      randomShear: boolean;
      randomScale: boolean;
      resize: boolean;
      randomRotate90: boolean;
      randomBrightnessContrast: boolean;
      gaussNoise: boolean;
      hueSaturationValue: boolean;
      blur: boolean;
      equalize: boolean;
      randomGamma: boolean;
      sharpen: boolean;
      toGray: boolean;
      toSepia: boolean;
      shiftScaleRotate: boolean;
      opticalDistortion: boolean;
      gridDistortion: boolean;
      transpose: boolean;
    };
    splitDataset: {
      id: string;
      train: number;
      validate: number;
      test: number;
    };
  };
  trainingResultJson: string;
  trainingResult: {
    progress: number;
    currentEpoch: number;
    totalEpochs: number;
    precision: number;
    recall: number;
    mAP: number;
  };
  createdBy: string;
  createdDate: string;
  modifiedBy: string;
  modifiedDate: string;
  projectId: string;
  project: {
    id: string;
    name: string;
    description: string;
    ownerId: string;
    sharedUsers: string[];
    folderId: string;
    folder: {
      id: string;
      name: string;
      description: string;
      ownerId: string;
      createdBy: string;
      createdDate: string;
      modifiedBy: string;
      modifiedDate: string;
      sharedUsers: string[];
      tenantId: string;
      customerId: string;
    };
    taskTypeId: string;
    domainTypeId: string;
    createdBy: string;
    createdDate: string;
    modifiedBy: string;
    modifiedDate: string;
    tenantId: string;
    customerId: string;
  };
  tenantId: string;
  customerId: string;
}

// Define the interface for the model
export interface Model {
  id: string; // UUID
  name: string;
  description: string;
  modelSize: number | null;
  supportEdgeDevice: boolean;
  taskTypeId: string; // UUID
  taskType: string | null; // Assuming it can be null or a string
}

export interface LabelCount {
  labelId: string;
  labelName: string;
  labelColor: string;
  count: number;
}

// types/interface/ProjectTrain.interface.ts
// types/interface/ProjectTrain.interface.ts
export interface ProjectTrainState {
  loading: boolean;
  error: string | null;
  dataModels: ProjectTrain[]; // Assuming this exists
  models: Model[]; // Use the Model interface here
  selectedProjectTrain: ProjectTrainDetail | null; // Set to null initially
  trainingStatus: TrainingStatus[]; // Add this property
  labelCount: LabelCount[];
  allModels: ProjectModelItem[];
}

export interface TrainingStatus {
  name: string;
  progress: number;
  status: string;
}

// Initial state
export const initialState: ProjectTrainState = {
  loading: false,
  error: null,
  dataModels: [],
  models: [],
  selectedProjectTrain: null,
  trainingStatus: [], // Add this to initialize training status
  labelCount: [], // Add this to store label count data
  allModels: [],
};

// export interface TestResponse {
//   predictions: any[]; // Adjust according to the actual structure of predictions
//   imageInfo: {
//     imageHeight: number;
//     imageWidth: number;
//   };
//   trainingId: string;
//   imageBase64: string;
//   labelInfo: LabelInfo[];
// }

export interface TestResponse {
  data?: {
    InferenceLink?: string;
  };
  message: string;
  status: string;
  timestamp: string;
  trainingId?: string;
  imageBase64?: string;
  labelInfo?: LabelInfo[];
}

export interface LabelInfo {
  lableName: string;
  count: number;
  color: string;
}

export interface TestState {
  testData: TestResponse | null;
  loading: boolean;
  error: string | null;
}

export const initialTestState: TestState = {
  testData: null,
  loading: false,
  error: null,
};

export interface TestRequestPayload {
  model: string;
  fileUpload: File;
  projectTitle: string;
  data?: {
    InferenceLink?: string;
  };
  trainingId?: string;
  imageBase64?: string;
  labelInfo?: LabelInfo[];
}

export interface DownloadTestResponse {
  success: boolean;
  duration: number;
}

export interface DownloadOptions {
  onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void; // Use AxiosProgressEvent
}

export interface ProjectModelItem {
  id: string;
  name: string;
  projectId: string;
  projectName: string;
  status: string | null;
  dateTime: string;
  taskType: string;
  modelSize: number | null;
  trainingName?: string;
  trainingId?: string;
  progress?: number;
  isPublishedToAILabel?: boolean
  isPublishedToInferenceServer?: boolean
  //isChecked: boolean;
}

// In your types/interface/ProjectTrain.interface.ts
